<script setup lang="ts">
/**
 * Render PSD — brief 03-procreate.md "Rendering" + 00-WORK.md §2. Replaces
 * the old "bring painted layers back as editable ink layers" import: pick a
 * PSD painted in Procreate/Photoshop, pick how many tiles across, get one
 * finished hi-res wallpaper PNG — the same two-step shape as Wallpaper
 * export, just sourced from a file instead of the live sketch.
 *
 * No existing toast/notification component in this codebase — the result
 * summary is shown inline in this sheet instead of a fire-and-forget alert,
 * so the user can actually read what happened before dismissing it.
 */
import { ref, watch } from 'vue';
import { IconAlertTriangle, IconCheck, IconUpload, IconX } from '@tabler/icons-vue';
import { useUiStore } from '@/stores/ui';
import { renderPsd, type PsdRenderSummary } from '@/services/psd/psdRender';

const ui = useUiStore();
const fileInput = ref<HTMLInputElement | null>(null);
const busy = ref(false);
const error = ref<string | null>(null);
const summary = ref<PsdRenderSummary | null>(null);

/** Step 2's control — same field/values as WallpaperExportPanel.vue, for consistency. */
const PRESETS = [2, 3, 4] as const;
const tilesAcross = ref(3);
const customOpen = ref(false);
const customValue = ref(5);

// Must be a ref: the step-2 UI is gated on it (`v-if="!buffer"`), so a plain
// `let` never re-renders and picking a file appears to do nothing.
const buffer = ref<ArrayBuffer | null>(null);

function close(): void {
  ui.showRenderPsd = false;
  buffer.value = null;
  summary.value = null;
  error.value = null;
  customOpen.value = false;
  tilesAcross.value = 3;
}

function pickFile(): void {
  fileInput.value?.click();
}

async function onFileChosen(e: Event): Promise<void> {
  const file = (e.target as HTMLInputElement).files?.[0];
  (e.target as HTMLInputElement).value = '';
  if (!file) return;
  error.value = null;
  buffer.value = await file.arrayBuffer();
}

function pickPreset(n: number): void {
  customOpen.value = false;
  tilesAcross.value = n;
}

function pickCustom(): void {
  customOpen.value = true;
  tilesAcross.value = customValue.value;
}

async function doRender(): Promise<void> {
  if (!buffer.value) return;
  busy.value = true;
  error.value = null;
  try {
    summary.value = await renderPsd(buffer.value, { tilesAcross: tilesAcross.value });
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Could not read that PSD file.';
  } finally {
    busy.value = false;
  }
}

// A PSD opened/shared into the app from the OS (openWithPsd.ts) — jump
// straight to rendering with the default tile count, same as picking a file
// and immediately pressing "Render wallpaper" (a failure still lands on the
// tiles-across step with the error shown, so it's never a dead end).
watch(
  () => ui.pendingPsdBuffer,
  (buf) => {
    if (!buf) return;
    ui.pendingPsdBuffer = null;
    summary.value = null;
    error.value = null;
    customOpen.value = false;
    tilesAcross.value = 3;
    buffer.value = buf;
    void doRender();
  },
);
</script>

<template>
  <div v-if="ui.showRenderPsd" class="sheet-backdrop" @click.self="close">
    <div class="sheet" role="dialog" aria-modal="true" aria-label="Render from PSD">
      <header>
        <h2>Render from PSD</h2>
        <button class="icon-close" aria-label="Close" @click="close">
          <IconX :size="18" stroke-width="2" />
        </button>
      </header>

      <template v-if="!summary">
        <p class="lead">
          Pick a PSD exported by Escherllate (or one built the same way) after painting on its
          "Paint - " layers in Procreate or Photoshop. Renders one finished, tiled wallpaper PNG —
          your current sketch is left exactly as it is.
        </p>

        <input ref="fileInput" type="file" accept=".psd,image/vnd.adobe.photoshop,application/octet-stream" class="hidden-input" @change="onFileChosen" />
        <button v-if="!buffer" class="primary-btn" @click="pickFile">
          <IconUpload :size="16" stroke-width="1.75" />
          Choose PSD file
        </button>

        <template v-else>
          <div class="seg">
            <button
              v-for="n in PRESETS"
              :key="n"
              class="seg-btn"
              :class="{ active: !customOpen && tilesAcross === n }"
              @click="pickPreset(n)"
            >
              {{ n }}×{{ n }}
            </button>
            <button class="seg-btn" :class="{ active: customOpen }" @click="pickCustom">Custom</button>
          </div>

          <label v-if="customOpen" class="custom-row">
            <span>Tiles across</span>
            <input v-model.number="customValue" type="number" min="1" max="12" @change="tilesAcross = customValue" />
          </label>

          <button class="primary-btn" :disabled="busy" @click="doRender">
            {{ busy ? 'Rendering…' : 'Render wallpaper' }}
          </button>
        </template>
      </template>

      <template v-else>
        <p v-if="summary.imported.length > 0" class="note">Wallpaper saved.</p>

        <div v-if="summary.imported.length > 0" class="result-block">
          <h3><IconCheck :size="15" stroke-width="2" /> Rendered</h3>
          <ul>
            <li v-for="name in summary.imported" :key="name">{{ name }}</li>
          </ul>
        </div>

        <div v-if="summary.missingRegions.length > 0" class="result-block warn">
          <h3><IconAlertTriangle :size="15" stroke-width="2" /> No painted layer found for</h3>
          <ul>
            <li v-for="name in summary.missingRegions" :key="name">{{ name }}</li>
          </ul>
          <p class="note">These regions were skipped — the rest of the wallpaper still rendered.</p>
        </div>

        <div v-if="summary.unmatched.length > 0" class="result-block">
          <h3>Included (no matching guide region)</h3>
          <ul>
            <li v-for="name in summary.unmatched" :key="name">{{ name }}</li>
          </ul>
        </div>

        <p v-if="summary.imported.length === 0" class="warn-msg">
          No painted content found in the PSD's "Paint - " layers — nothing to render, so no file was saved.
        </p>

        <button class="primary-btn" @click="close">Done</button>
      </template>

      <!-- Errors surface in every state (not only step 1), so a failure is never silent. -->
      <p v-if="error" class="error">{{ error }}</p>
    </div>
  </div>
</template>

<style scoped>
.sheet-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(8, 8, 16, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.sheet {
  width: min(440px, calc(100vw - 32px));
  max-height: calc(100vh - 64px);
  overflow-y: auto;
  background: var(--ui-surface);
  border: 1px solid var(--ui-border);
  border-radius: 12px;
  padding: 20px;
}

header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

h2 {
  margin: 0;
  font-size: 18px;
  color: var(--text-primary);
}

.icon-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: transparent;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
}

.icon-close:hover {
  color: var(--text-primary);
}

.lead {
  margin: 0 0 14px;
  font-size: 13px;
  color: var(--text-muted);
  line-height: 1.5;
}

.hidden-input {
  display: none;
}

.primary-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  min-height: 42px;
  background: var(--accent);
  color: var(--accent-contrast);
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-top: 10px;
}

.primary-btn:disabled {
  opacity: 0.6;
}

.seg {
  display: flex;
  gap: 2px;
  background: color-mix(in srgb, var(--ui-border) 35%, transparent);
  border: 1px solid var(--ui-border);
  border-radius: 9px;
  padding: 2px;
}

.seg-btn {
  flex: 1;
  min-height: 34px;
  padding: 4px 14px;
  font-size: 13px;
  background: transparent;
  color: var(--text-primary);
  border: none;
  border-radius: 7px;
  cursor: pointer;
}

.seg-btn.active {
  background: color-mix(in srgb, var(--accent) 22%, transparent);
}

.custom-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  color: var(--text-primary);
  margin-top: 10px;
}

.custom-row input {
  width: 64px;
  min-height: 32px;
  padding: 4px 8px;
  background: var(--canvas-bg);
  border: 1px solid var(--ui-border);
  border-radius: 6px;
  color: var(--text-primary);
}

.error {
  margin: 10px 0 0;
  font-size: 13px;
  color: var(--danger);
}

.warn-msg {
  margin: 0 0 12px;
  font-size: 13px;
  line-height: 1.45;
  color: #d9a441;
}

.result-block {
  margin-bottom: 14px;
}

.result-block h3 {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 6px;
  font-size: 13px;
  color: var(--text-primary);
}

.result-block.warn h3 {
  color: #d9a441;
}

.result-block ul {
  margin: 0;
  padding-left: 20px;
  font-size: 12.5px;
  color: var(--text-muted);
}

.note {
  font-size: 12.5px;
  color: var(--text-muted);
  margin: 0 0 12px;
}
</style>
